/**
 * ── DOES THIS LINE FIT? MEASURED, NOT COUNTED (2026-08-28) ──────────────────────
 *
 * `ECH_CommunitySubHead` is one line of 40px Times New Roman in a 520px column, and
 * Layne's guardrail says so in as many words:
 *
 *   "ONE LINE ONLY… At 40px Times New Roman in a 520px column, one line holds about 30
 *    characters… The real constraint is pixel width, not character count — 520px. A name
 *    in all caps eats the width much faster: 14 capital Ws would already overflow."
 *
 * The block declares `cpl: 30`, so the panel counts characters — which is the proxy she
 * explicitly warns about. "MMMMMMMMMMMMMM" is 14 characters and does not fit;
 * "The Reserve at Lake Nona" is 24 and fits with 93px to spare. This measures the ink.
 *
 * WHY A TABLE AND NOT A CANVAS. `measureText` needs a real canvas — jsdom has none, so a
 * canvas-based check would be untestable and would silently pass in CI. These are the
 * published Times New Roman advance widths (units per 1000 em), and they reproduce all
 * four widths Layne measured herself to within half a pixel:
 *
 *   Sunrise Ridge 225.6 (she measured 226) · Windermere Sound 312.2 (312)
 *   The Reserve at Lake Nona 426.5 (427) · Heritage at Vizcaya Preserve 465.4 (465)
 *
 * and 14 capital Ws come out at 528.6px — over the 520 limit, exactly as she says. Those
 * five numbers are the test, so a change to this table has to keep agreeing with her.
 *
 * WHAT IT IS NOT FOR: shrinking the font. The same guardrail — "Do not reduce the font
 * size, reduce the line-height, or widen the column to make room" — rules that out. The
 * only correct answer to an overflow is shorter wording, so this exists to SAY SO before
 * the email ships, not to paper over it.
 */

/** Times New Roman advance widths, units per 1000 em (Adobe AFM). */
const W: Record<string, number> = {
  " ": 250, "!": 333, '"': 408, "#": 500, $: 500, "%": 833, "&": 778, "'": 180,
  "(": 333, ")": 333, "*": 500, "+": 564, ",": 250, "-": 333, ".": 250, "/": 278,
  "0": 500, "1": 500, "2": 500, "3": 500, "4": 500, "5": 500, "6": 500, "7": 500,
  "8": 500, "9": 500, ":": 278, ";": 278, "<": 564, "=": 564, ">": 564, "?": 444, "@": 921,
  A: 722, B: 667, C: 667, D: 722, E: 611, F: 556, G: 722, H: 722, I: 333, J: 389,
  K: 722, L: 611, M: 889, N: 722, O: 722, P: 556, Q: 722, R: 667, S: 556, T: 611,
  U: 722, V: 722, W: 944, X: 722, Y: 722, Z: 611, "[": 333, "\\": 278, "]": 333,
  "^": 469, _: 500, "`": 333,
  a: 444, b: 500, c: 444, d: 500, e: 444, f: 333, g: 500, h: 500, i: 278, j: 278,
  k: 500, l: 278, m: 778, n: 500, o: 500, p: 500, q: 500, r: 333, s: 389, t: 278,
  u: 500, v: 500, w: 722, x: 500, y: 500, z: 444, "{": 480, "|": 200, "}": 480, "~": 541,
  /* the punctuation a community name actually carries beyond ASCII */
  "’": 333, "‘": 333, "“": 444, "”": 444, "–": 500, "—": 1000,
};

/** an unknown glyph is charged the width of a digit — mid-range, never free */
const DEFAULT_W = 500;

/** the rendered width of `text` in Times New Roman at `size` px */
export function timesPx(text: unknown, size = 40): number {
  const t = String(text == null ? "" : text);
  let u = 0;
  for (let i = 0; i < t.length; i++) u += W[t[i]] ?? DEFAULT_W;
  return Math.round((u * size) / 1000 * 10) / 10;
}

/** the subhead's own box: 600px block, 40px padding each side (both locked) */
export const SUBHEAD_MAX_PX = 520;
export const SUBHEAD_SIZE_PX = 40;

export interface FitResult {
  /** the measured width of the line */
  px: number;
  /** how much room is left; negative when it overflows */
  slack: number;
  fits: boolean;
}

/** does this community subhead hold on one line? */
export function subheadFit(text: unknown): FitResult {
  const px = timesPx(text, SUBHEAD_SIZE_PX);
  return { px, slack: Math.round((SUBHEAD_MAX_PX - px) * 10) / 10, fits: px <= SUBHEAD_MAX_PX };
}

/**
 * What to say when it does not fit. Names the real constraint and the only sanctioned
 * fix — shorter wording — because the block forbids every other one.
 */
export function subheadOverflowMessage(text: unknown): string {
  const f = subheadFit(text);
  if (f.fits) return "";
  return "This runs " + Math.abs(f.slack) + "px past the one line this block allows ("
    + f.px + "px of " + SUBHEAD_MAX_PX + "px). Shorten the wording — the font size and the "
    + "column width are locked.";
}

/**
 * ── WHICH FIELDS ARE PHYSICALLY ONE LINE ─────────────────────────────────────────
 *
 * Data, not a special case in the panel: a block whose guardrail states a locked font,
 * a locked column and ONE LINE gets a row here, and the Inspector asks this table
 * rather than knowing about any particular block. Today that is the community subhead;
 * the Grand Opening headline is the obvious next one if Layne locks its type the same
 * way.
 */
export interface OneLineRule { field: string; maxPx: number; sizePx: number }

const ONE_LINE: Record<string, OneLineRule> = {
  ECH_CommunitySubHead: { field: "subhead", maxPx: SUBHEAD_MAX_PX, sizePx: SUBHEAD_SIZE_PX },
};

/** the one-line rule for a block field, or null */
export function oneLineRule(blockName: unknown, fieldKey: unknown): OneLineRule | null {
  const r = ONE_LINE[String(blockName || "")];
  return r && r.field === String(fieldKey || "") ? r : null;
}

/** "" when it fits (or has no rule) — otherwise the sentence to show under the field */
export function oneLineOverflow(blockName: unknown, fieldKey: unknown, value: unknown): string {
  const r = oneLineRule(blockName, fieldKey);
  if (!r) return "";
  const px = timesPx(value, r.sizePx);
  if (px <= r.maxPx) return "";
  return "This runs " + Math.round((px - r.maxPx) * 10) / 10 + "px past the one line this block "
    + "allows (" + px + "px of " + r.maxPx + "px). Shorten the wording — the font size and the "
    + "column width are locked.";
}
